/* eslint-disable */
/**
 * Pure utility functions for the Urban Dashboard.
 * These functions handle flag reading, number formatting (Indian numbering),
 * label transformation, state detection, error categorization, and accessibility.
 */

/**
 * Reads the IsUrban flag from localStorage and returns true only for 'true' or '1'.
 * All other values (null, undefined, empty string, '0', 'false', etc.) return false.
 * @returns {boolean}
 */
export function getIsUrbanFlag() {
    const value = localStorage.getItem('IsUrban');
    return value === 'true' || value === '1';
}

/**
 * Formats a number as Indian currency with ₹ prefix and 2 decimal places.
 * Uses Indian numbering system: first group of 3 from right, then groups of 2.
 * Example: 123456.5 → "₹1,23,456.50"
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted currency string
 */
export function formatIndianCurrency(value) {
    const num = Number(value);
    const fixed = num.toFixed(2);
    const [integerPart, decimalPart] = fixed.split('.');

    const formattedInteger = formatWithIndianCommas(integerPart);
    return `₹${formattedInteger}.${decimalPart}`;
}

/**
 * Formats a number as an Indian-style count (whole number with Indian commas).
 * Uses Indian numbering system: first group of 3 from right, then groups of 2.
 * Example: 123456 → "1,23,456"
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted count string
 */
export function formatIndianCount(value) {
    const num = Math.round(Number(value));
    const integerStr = String(num);
    return formatWithIndianCommas(integerStr);
}

/**
 * Converts a snake_case field name to a Title Case label.
 * Example: "active_merchants" → "Active Merchants"
 * @param {string} fieldName - The snake_case field name
 * @returns {string} Title Case label
 */
export function fieldNameToLabel(fieldName) {
    if (!fieldName) return '';
    return fieldName
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Returns true if the field name ends with '_volume'.
 * @param {string} fieldName - The field name to check
 * @returns {boolean}
 */
export function isVolumeField(fieldName) {
    if (!fieldName || typeof fieldName !== 'string') return false;
    return fieldName.endsWith('_volume');
}

/**
 * Returns true if every numeric value in the count object is 0 or null/undefined.
 * Non-numeric fields are ignored.
 * @param {object} count - The count object from the API response
 * @returns {boolean}
 */
export function isAllMetricsEmpty(count) {
    if (!count || typeof count !== 'object') return true;

    const values = Object.values(count);
    if (values.length === 0) return true;

    return values.every((val) => val === null || val === undefined || val === 0);
}

/**
 * Categorizes an error into one of: 'network', 'timeout', 'server', 'session'.
 * Returns an object with { category, message }.
 * @param {Error|object} error - The error object (typically from Axios)
 * @returns {{ category: string, message: string }}
 */
export function categorizeError(error) {
    if (!error) {
        return { category: 'server', message: 'Something went wrong. Please try again later.' };
    }

    // Session error: missing token
    if (error.isSessionError || error.message === 'Session unavailable') {
        return {
            category: 'session',
            message: 'Session unavailable. Please log in again.'
        };
    }

    // Network error: no response received
    if (error.message === 'Network Error' || (error.request && !error.response)) {
        return {
            category: 'network',
            message: 'Network unavailable. Check your connection.'
        };
    }

    // Timeout error
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
            category: 'timeout',
            message: 'Request timed out. Please try again.'
        };
    }

    // Server error: status >= 500
    if (error.response && error.response.status >= 500) {
        return {
            category: 'server',
            message: 'Server error. Please try again later.'
        };
    }

    // Default to server error for any other case
    return { category: 'server', message: 'Something went wrong. Please try again later.' };
}

/**
 * Builds an ARIA label string from the metric label and its formatted value.
 * Format: "{label}: {formattedValue}"
 * @param {string} label - The metric display name
 * @param {string} formattedValue - The formatted value string
 * @returns {string} ARIA label string
 */
export function buildAriaLabel(label, formattedValue) {
    return `${label}: ${formattedValue}`;
}

// --- Internal helper ---

/**
 * Applies Indian numbering commas to an integer string.
 * First group of 3 from the right, then groups of 2.
 * Handles negative numbers by preserving the leading '-'.
 * @param {string} integerStr - The integer part as a string
 * @returns {string} Comma-formatted string
 */
function formatWithIndianCommas(integerStr) {
    let isNegative = false;
    let str = integerStr;

    if (str.startsWith('-')) {
        isNegative = true;
        str = str.slice(1);
    }

    if (str.length <= 3) {
        return isNegative ? `-${str}` : str;
    }

    // Last 3 digits
    const lastThree = str.slice(-3);
    const remaining = str.slice(0, -3);

    // Group remaining digits in pairs from the right
    const pairs = [];
    for (let i = remaining.length; i > 0; i -= 2) {
        const start = Math.max(0, i - 2);
        pairs.unshift(remaining.slice(start, i));
    }

    const formatted = pairs.join(',') + ',' + lastThree;
    return isNegative ? `-${formatted}` : formatted;
}
